import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import GetAppIcon from '@material-ui/icons/GetApp';
import AddIcon from '@material-ui/icons/Add';

import Layout from 'Components/Layout';
import { notifyErrorAction } from '../../../common/actions/notifications';
import TopBar from '../../components/TopBar';

import DropZone from './DropZone';
import Cropper from './Cropper';
import AddToFootdataDialog from './AddToFootdataDialog';
import {
  decodeImage,
  maybePreResize,
  buildPreviewDataUrl,
  exportCrop,
  buildOutputFilename,
  TARGET_W,
  TARGET_H,
} from './exportImage';
import styles from './styles';

const STEP_UPLOAD = 'upload';
const STEP_LOADING = 'loading';
const STEP_CROP = 'crop';
const STEP_EXPORTING = 'exporting';
const STEP_VALIDATED = 'validated';

@injectIntl
@connect()
@withStyles(styles)
export default class ImageResize extends Component {
  state = {
    step: STEP_UPLOAD,
    sourceFile: null,
    sourceBitmap: null,
    sourceUrl: null,
    sourceDims: null,
    crop: null,
    previewUrl: null,
    outputBlob: null,
    outputUrl: null,
    outputFilename: null,
    dialogOpen: false,
    cropperKey: 0,
  };

  previewTimer = null;

  componentWillUnmount() {
    this.cleanupSource();
    this.cleanupOutput();
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  cleanupSource = () => {
    const { sourceUrl } = this.state;
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
  };

  cleanupOutput = () => {
    const { outputUrl } = this.state;
    if (outputUrl) URL.revokeObjectURL(outputUrl);
  };

  notifyError = key => {
    this.props.dispatch(notifyErrorAction(key));
  };

  onFile = async file => {
    this.cleanupSource();
    this.cleanupOutput();
    this.setState({
      step: STEP_LOADING,
      sourceFile: file,
      sourceUrl: null,
      sourceBitmap: null,
      sourceDims: null,
      crop: null,
      previewUrl: null,
      outputBlob: null,
      outputUrl: null,
    });
    try {
      let bitmap = await decodeImage(file);
      bitmap = await maybePreResize(bitmap);
      const width = bitmap.naturalWidth || bitmap.width;
      const height = bitmap.naturalHeight || bitmap.height;
      const blob = await new Promise(resolve => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        canvas.toBlob(resolve, 'image/jpeg', 0.95);
      });
      const sourceUrl = URL.createObjectURL(blob || file);
      this.setState({
        step: STEP_CROP,
        sourceBitmap: bitmap,
        sourceUrl,
        sourceDims: { width, height },
      });
    } catch (err) {
      this.notifyError('tools_imageResize_invalidFile');
      this.setState({ step: STEP_UPLOAD, sourceFile: null });
    }
  };

  onUploadErrorMessage = messageKey => {
    this.notifyError(messageKey);
  };

  onCropChange = crop => {
    this.setState({ crop });
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(this.refreshPreview, 120);
  };

  refreshPreview = async () => {
    const { sourceBitmap, crop } = this.state;
    if (!sourceBitmap || !crop) return;
    try {
      const dataUrl = await buildPreviewDataUrl(sourceBitmap, crop);
      this.setState({ previewUrl: dataUrl });
    } catch (_) {
      // ignore preview errors, real export will catch it
    }
  };

  onValidateCrop = async () => {
    const { sourceBitmap, crop, sourceFile } = this.state;
    if (!sourceBitmap || !crop) return;
    this.setState({ step: STEP_EXPORTING });
    try {
      const { blob } = await exportCrop(sourceBitmap, crop);
      const url = URL.createObjectURL(blob);
      this.setState({
        step: STEP_VALIDATED,
        outputBlob: blob,
        outputUrl: url,
        outputFilename: buildOutputFilename(sourceFile && sourceFile.name),
      });
    } catch (err) {
      this.notifyError('tools_imageResize_dialog_error');
      this.setState({ step: STEP_CROP });
    }
  };

  onChangeImage = () => {
    this.cleanupSource();
    this.cleanupOutput();
    this.setState({
      step: STEP_UPLOAD,
      sourceFile: null,
      sourceBitmap: null,
      sourceUrl: null,
      sourceDims: null,
      crop: null,
      previewUrl: null,
      outputBlob: null,
      outputUrl: null,
    });
  };

  onRestartFromValidated = () => {
    this.cleanupOutput();
    this.setState({
      step: STEP_CROP,
      outputBlob: null,
      outputUrl: null,
    });
  };

  onReset = () => {
    this.setState(({ cropperKey }) => ({ cropperKey: cropperKey + 1 }));
  };

  openDialog = () => this.setState({ dialogOpen: true });
  closeDialog = () => this.setState({ dialogOpen: false });

  renderUpload() {
    return (
      <DropZone
        onFile={this.onFile}
        onError={this.onUploadErrorMessage}
      />
    );
  }

  renderLoading() {
    const { classes, intl: { messages: m } } = this.props;
    return (
      <div className={classes.loading}>
        <CircularProgress />
        <Typography variant="body2">{m.tools_imageResize_loading}</Typography>
      </div>
    );
  }

  renderCrop() {
    const { classes, intl: { messages: m } } = this.props;
    const { sourceUrl, sourceDims, crop, previewUrl, step, cropperKey } = this.state;
    const tooSmall =
      sourceDims &&
      (sourceDims.width < TARGET_W || sourceDims.height < TARGET_H);
    return (
      <div className={classes.editor}>
        <div className={classes.cropperPane}>
          <Cropper
            key={cropperKey}
            src={sourceUrl}
            onCropChange={this.onCropChange}
          />
        </div>
        <div className={classes.sidePane}>
          <Typography variant="subtitle2">
            {m.tools_imageResize_outputLabel}
          </Typography>
          <div className={classes.previewBox}>
            {previewUrl && (
              <img
                src={previewUrl}
                alt=""
                className={classes.previewImg}
              />
            )}
          </div>
          <div className={classes.info}>
            <div>
              <span className={classes.infoLabel}>
                {m.tools_imageResize_sourceLabel}:
              </span>{' '}
              {sourceDims
                ? `${sourceDims.width} × ${sourceDims.height}`
                : '—'}
            </div>
            <div>
              <span className={classes.infoLabel}>
                {m.tools_imageResize_outputLabel}:
              </span>{' '}
              {TARGET_W} × {TARGET_H} JPG
            </div>
            {crop && crop.width > 0 && (
              <div>
                <span className={classes.infoLabel}>Crop:</span>{' '}
                {Math.round(crop.width)} × {Math.round(crop.height)}
              </div>
            )}
            {tooSmall && (
              <div className={classes.warning}>
                {m.tools_imageResize_warning_tooSmall}
              </div>
            )}
          </div>
          <div className={classes.actionsRow}>
            <Button onClick={this.onReset}>
              {m.tools_imageResize_reset}
            </Button>
            <Button onClick={this.onChangeImage}>
              {m.tools_imageResize_changeImage}
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!crop || step === STEP_EXPORTING}
              onClick={this.onValidateCrop}
            >
              {step === STEP_EXPORTING ? (
                <CircularProgress size={20} />
              ) : (
                m.tools_imageResize_validateCrop
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  renderValidated() {
    const { classes, intl: { messages: m } } = this.props;
    const { outputUrl, outputBlob, outputFilename, dialogOpen } = this.state;
    const sizeKb = outputBlob ? Math.round(outputBlob.size / 1024) : 0;
    return (
      <div className={classes.validatedRoot}>
        <div className={classes.validatedPreview}>
          {outputUrl && (
            <img
              src={outputUrl}
              alt=""
              className={classes.validatedPreviewImg}
            />
          )}
        </div>
        <Typography variant="body2" className={classes.info}>
          {TARGET_W} × {TARGET_H} JPG — {sizeKb} Ko
        </Typography>
        <div className={classes.validatedActions}>
          <Button
            color="primary"
            variant="contained"
            startIcon={<GetAppIcon />}
            component="a"
            href={outputUrl}
            download={outputFilename}
          >
            {m.tools_imageResize_download}
          </Button>
          <Button
            color="primary"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={this.openDialog}
          >
            {m.tools_imageResize_addToFootdata}
          </Button>
          <Button onClick={this.onRestartFromValidated}>
            {m.tools_imageResize_restart}
          </Button>
          <Button onClick={this.onChangeImage}>
            {m.tools_imageResize_changeImage}
          </Button>
        </div>
        {outputBlob && dialogOpen && (
          <AddToFootdataDialog
            open={dialogOpen}
            blob={outputBlob}
            filename={outputFilename}
            onClose={this.closeDialog}
          />
        )}
      </div>
    );
  }

  renderBody() {
    const { step } = this.state;
    if (step === STEP_UPLOAD) return this.renderUpload();
    if (step === STEP_LOADING) return this.renderLoading();
    if (step === STEP_CROP || step === STEP_EXPORTING) return this.renderCrop();
    if (step === STEP_VALIDATED) return this.renderValidated();
    return null;
  }

  render() {
    const { classes, intl: { messages: m } } = this.props;
    return (
      <Layout
        id="image-resize-layout"
        top={
          <TopBar
            crumbStack={[
              { labelKey: 'app_tools' },
              { labelKey: 'app_tools_imageResize' },
            ]}
          />
        }
        main={
          <Paper elevation={2} className={classes.paper}>
            <Typography variant="h6" className={classes.title}>
              {m.app_tools_imageResize} — 16/9 {TARGET_W}×{TARGET_H}
            </Typography>
            {this.renderBody()}
          </Paper>
        }
      />
    );
  }
}
